import { t } from '@lingui/core/macro';
import * as Sentry from '@sentry/react';
import { Navigate, SearchParamError, useLocation } from '@tanstack/react-router';
import { ErrorPageContainer } from 'components/ErrorPageContainer';
import { useIsAppOutdated } from 'hooks/useIsAppOutdated';
import { isEmpty } from 'lodash';
import { type FC, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useReloadStore } from 'stores/reloadStore';

type Props = {
  error?: Error;
};

const DefaultErrorPage: FC<Props> = ({ error }) => {
  const location = useLocation();
  const { data: isAppOutdated } = useIsAppOutdated();
  const { blocked, reload } = useReloadStore();

  useEffect(() => {
    if (isAppOutdated) {
      if (blocked) {
        Sentry.captureMessage('Auto-reload blocked to prevent reload loop');
      } else {
        reload();
      }
    }
  }, [isAppOutdated, reload, blocked]);

  if (isAppOutdated && !blocked) {
    return null;
  }

  if (error instanceof SearchParamError && !isEmpty(location.search)) {
    toast.info(
      t`Certains paramètres de recherche de l’url n’ont pas pu être décodés. Les valeurs par défaut ont été appliquées.`,
      { toastId: 'invalid-search-params' }
    );
    return <Navigate to={location.pathname} />;
  }

  return (
    <ErrorPageContainer
      title={t`Une erreur est survenue`}
      subtitle={t`Il peut s’agir d’un problème temporaire de connexion ou d’une mise à jour récente. Essayez de rafraîchir la page. Si le problème persiste, contactez l’assistance.`}
      buttonOptions={{
        children: t`Rafraîchir la page`,
        onClick: () => window.location.reload(), // don't use the reload store here, because this is user initiated action
      }}
    />
  );
};

export default DefaultErrorPage;
